import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { CloseButton } from "@jetblue/kit/close-button";
import { IcAlert } from "@jetblue/kit-icons/ic-alert";
import "./travel-alert.css";
import { cn } from "../../utilities/tailwind";

export type TravelAlertProperties = ComponentPropsWithoutRef<"div"> & {
  /**
   * **Required.** Displays node with intended alert for travelers.
   */
  children: ReactNode;
  className?: string | undefined;
  hasWhiteText?: boolean | undefined;
  /**
   * Allows the parent component to open or close the `TravelAlert`.
   */
  isOpen?: boolean | undefined;
  /**
   * Callback invoked with button onClick.
   */
  onCloseClick?: (() => void) | undefined;
  /**
   * Defaults to true. When false, removes the `CloseButton` from the DOM.
   */
  showCloseButton?: boolean | undefined;
};
export const TravelAlert = ({
  children,
  className = "",
  hasWhiteText,
  isOpen = true,
  onCloseClick,
  showCloseButton = true,
  ...properties
}: TravelAlertProperties) => {
  const twClassName = cn(
    "group/travel-alert flex min-h-11 min-w-72 flex-row items-center bg-surface-container px-4 text-on-surface data-[dark-bg=true]:bg-primary data-[dark-bg=true]:text-on-primary md:px-8 lg:px-10",
    // eslint-disable-next-line better-tailwindcss/no-unknown-classes -- this is loaded in this file
    { "closed-travel-alert": !isOpen },
    className,
  );

  return (
    <div className={twClassName} data-dark-bg={hasWhiteText} {...properties}>
      <div className="flex w-full items-center justify-center py-1 pr-4 md:pr-0">
        <span className="me-2">
          <IcAlert
            aria-hidden
            className="size-8 group-data-[dark-bg=true]/travel-alert:fill-on-primary"
          />
        </span>

        <div className="text-s-body [&_a]:text-primary [&_a]:focus-outline group-data-[dark-bg=true]/travel-alert:[&_a]:text-on-primary group-data-[dark-bg=true]/travel-alert:[&_a]:outline-on-primary [&_a]:hover:underline">
          {children}
        </div>
      </div>

      {showCloseButton && (
        <CloseButton
          onPress={onCloseClick}
          shape="circle-borderless"
          size="small"
          variant={hasWhiteText ? "blue" : "white"}
        />
      )}
    </div>
  );
};
TravelAlert.displayName = "TravelAlert";
